import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { money } from "../lib/format";
import { useBasket } from "../context/BasketContext";

interface Status { purpose: "topup" | "order" | "club_gift"; gift_code?: string | null; gift?: { recipient_name: string; tier: string; cases: number } | null; amount_pence: number; payment_status: string; fulfilled: boolean; order_error?: string | null; order?: { id: string; shopify_order_name?: string | null; status: string; total_pence: number }; wallet: { balance_pence: number } }

export default function PaymentReturnPage() {
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const { clear } = useBasket();
  const sessionId = params.get("session_id");
  const [state, setState] = useState<Status | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get<Status>(`/stripe/status/${sessionId}`);
        if (data.payment_status === "paid" && data.fulfilled) {
          setState(data);
          if (data.purpose === "order" && data.order) clear();
          ["wallet", "ledger", "orders", "payments", "stripe-transactions"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState(data); return; }
      } catch (e) { console.warn("stripe status poll failed, retrying", e); }
      if (attempts >= 15) { setTimedOut(true); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, qc, clear]);

  const done = state && state.payment_status === "paid";
  return (
    <div className="mx-auto max-w-lg py-24 text-center" data-testid="payment-return-page">
      <div className="eyebrow">Card payment</div>
      {!state && !timedOut && (
        <>
          <h1 className="mt-4 font-serif text-4xl font-medium">Confirming your payment</h1>
          <p className="mt-4 text-sm text-neutral-600" data-testid="payment-return-message">Talking to Stripe — this only takes a moment.</p>
          <div className="mx-auto mt-10 h-px w-40 animate-pulse bg-[var(--ink)]" />
        </>
      )}
      {timedOut && !state && (
        <>
          <h1 className="mt-4 font-serif text-4xl font-medium">Still confirming</h1>
          <p className="mt-4 text-sm text-neutral-600" data-testid="payment-return-message">Your payment is taking longer than usual. If it went through, your cellar will be credited automatically — check back in a moment.</p>
          <Link to="/cellar" className="mt-8 inline-block text-sm underline underline-offset-4" data-testid="payment-return-cellar-link">Go to your cellar</Link>
        </>
      )}
      {state && !done && (
        <>
          <h1 className="mt-4 font-serif text-4xl font-medium">Payment not completed</h1>
          <p className="mt-4 text-sm text-neutral-600" data-testid="payment-return-message">Nothing has been charged and your cellar is unchanged.</p>
          <Link to="/cellar" className="mt-8 inline-block text-sm underline underline-offset-4" data-testid="payment-return-cellar-link">Back to your cellar</Link>
        </>
      )}
      {done && state.purpose === "topup" && (
        <>
          <h1 className="mt-4 font-serif text-4xl font-medium">Cellar credited</h1>
          <p className="mt-4 text-sm text-neutral-600" data-testid="payment-return-message">{money(state.amount_pence)} has been added. Your balance is now <span className="tabular-nums" data-testid="payment-return-balance">{money(state.wallet.balance_pence)}</span>.</p>
          <div className="mt-8 flex justify-center gap-6 text-sm">
            <Link to="/shop" className="underline underline-offset-4" data-testid="payment-return-shop-link">Browse wines</Link>
            <Link to="/cellar?tab=ledger" className="underline underline-offset-4" data-testid="payment-return-cellar-link">View ledger</Link>
          </div>
        </>
      )}
      {done && state.purpose === "club_gift" && (
        <>
          <h1 className="mt-4 font-serif text-4xl font-medium" data-testid="payment-return-gift-title">{state.order_error ? "Payment received" : `Your gift to ${state.gift?.recipient_name || "them"} is on its way`}</h1>
          <p className="mt-4 text-sm text-neutral-600" data-testid="payment-return-message">{state.order_error ? `We've taken ${money(state.amount_pence)} but couldn't finish the gift automatically (${state.order_error}). The team has been notified and will sort it.` : <>{money(state.amount_pence)} paid by card. Gift code <strong>{state.gift_code}</strong>. We've emailed {state.gift?.recipient_name} and our team will arrange the first case.</>}</p>
          <div className="mt-8 flex justify-center gap-6 text-sm"><Link to="/gift-club" className="underline underline-offset-4" data-testid="payment-return-gift-link">Gift another</Link><Link to="/" className="underline underline-offset-4">Back to overview</Link></div>
        </>
      )}
      {done && state.purpose === "order" && state.order && (
        <>
          <h1 className="mt-4 font-serif text-4xl font-medium">Order placed</h1>
          <p className="mt-4 text-sm text-neutral-600" data-testid="payment-return-message">Order <span data-testid="payment-return-order-name">{state.order.shopify_order_name || state.order.id.slice(-6).toUpperCase()}</span> · {money(state.order.total_pence)} paid by card. We'll email you when it ships.</p>
          <Link to="/orders" className="mt-8 inline-block text-sm underline underline-offset-4" data-testid="payment-return-orders-link">Track your order</Link>
        </>
      )}
      {done && state.purpose === "order" && !state.order && (
        <>
          <h1 className="mt-4 font-serif text-4xl font-medium">Payment received</h1>
          <p className="mt-4 text-sm text-[var(--wine)]" data-testid="payment-return-message">{state.order_error || "We couldn't place the order with our fulfilment system."} Your {money(state.amount_pence)} is safe as cellar credit — try again from your case, or contact us.</p>
          <Link to="/cellar" className="mt-8 inline-block text-sm underline underline-offset-4" data-testid="payment-return-cellar-link">Go to your cellar</Link>
        </>
      )}
    </div>
  );
}
